import React, { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_PANEL_DURATION } from "../utils/storyboardIO";
import styles from "./StoryboardTimelinePlayer.module.css";

const formatSeconds = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const StoryboardTimelinePlayer = ({ storyboard }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const playerRef = useRef(null);

  const allPanels = useMemo(
    () =>
      storyboard.flatMap((page) =>
        page.panels.map((panel) => ({ ...panel, pageTitle: page.title }))
      ),
    [storyboard]
  );

  const totalDuration = useMemo(
    () => allPanels.reduce((acc, p) => acc + (p.duration || DEFAULT_PANEL_DURATION), 0),
    [allPanels]
  );
  const elapsedDuration = useMemo(
    () =>
      allPanels
        .slice(0, currentIndex)
        .reduce((acc, p) => acc + (p.duration || DEFAULT_PANEL_DURATION), 0),
    [allPanels, currentIndex]
  );

  // Si el storyboard cambia (se borró un frame, etc.) evita índices fuera de rango.
  useEffect(() => {
    if (currentIndex >= allPanels.length) setCurrentIndex(0);
  }, [allPanels, currentIndex]);

  // Avanza automáticamente respetando la duración propia de cada frame.
  useEffect(() => {
    if (!isPlaying || allPanels.length === 0) return undefined;
    const duration =
      (allPanels[currentIndex]?.duration || DEFAULT_PANEL_DURATION) * 1000;
    const timeout = setTimeout(() => {
      setCurrentIndex((prev) => (prev < allPanels.length - 1 ? prev + 1 : 0));
    }, duration);
    return () => clearTimeout(timeout);
  }, [isPlaying, currentIndex, allPanels]);

  const togglePlay = () => setIsPlaying((prev) => !prev);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await playerRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn("Fullscreen bloqueado:", err);
    }
  };

  if (allPanels.length === 0) {
    return (
      <div className={styles.empty}>Agrega frames al storyboard para poder reproducirlos.</div>
    );
  }

  const current = allPanels[currentIndex];

  return (
    <div ref={playerRef} className={styles.player}>
      <div className={styles.stage}>
        <div className={styles.stageOverlayTop}>
          <span className="text-mono-sm">{current.pageTitle}</span>
          <span className="text-mono-sm">
            FRAME {currentIndex + 1}/{allPanels.length}
          </span>
        </div>

        <img src={current.image} alt={current.text} className={styles.stageImage} />

        {current.text && (
          <div className={`${styles.caption} text-body-md`}>{current.text}</div>
        )}

        <div className={styles.stageOverlayBottom}>
          <span className="text-mono-sm">
            {formatSeconds(elapsedDuration)} / {formatSeconds(totalDuration)}
          </span>
        </div>
      </div>

      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${(elapsedDuration / (totalDuration || 1)) * 100}%` }}
        />
      </div>

      <div className={styles.controls}>
        <button className={styles.controlButton} onClick={() => setCurrentIndex(0)} title="Ir al inicio">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>skip_previous</span>
        </button>
        <button className={styles.playButton} onClick={togglePlay} title={isPlaying ? "Pausar" : "Reproducir"}>
          <span className="material-symbols-outlined fill" style={{ fontSize: 22 }}>
            {isPlaying ? "pause" : "play_arrow"}
          </span>
        </button>
        <button
          className={styles.controlButton}
          onClick={() => setCurrentIndex(allPanels.length - 1)}
          title="Ir al final"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>skip_next</span>
        </button>

        <div className={styles.spacer} />

        <button className={styles.controlButton} onClick={toggleFullscreen} title="Pantalla completa">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>fullscreen</span>
        </button>
      </div>

      <div className={`${styles.filmstrip} themed-scroll`}>
        {allPanels.map((panel, index) => (
          <button
            key={panel.id}
            className={`${styles.thumb} ${index === currentIndex ? styles.thumbActive : ""}`}
            onClick={() => setCurrentIndex(index)}
          >
            <img src={panel.image} alt={panel.text} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default StoryboardTimelinePlayer;
